import { useState } from 'react'
import { Database, Wifi, RotateCcw } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  clearStoredPreference,
  invalidateAppEnvCache,
  readAppEnvFresh,
  readStoredPreference,
  writeStoredPreference,
  type AppMode,
} from '@/lib/env'
import { cn } from '@/lib/utils'

/**
 * Badge clicável fixo no canto inferior direito que sinaliza qual modo o
 * app está rodando E permite trocar para o outro modo sem precisar parar
 * o `pnpm dev`.
 *
 * Visível em DEV E PROD (decisão da VEC-141 v2):
 * - DEV: ferramenta de debug — alternar entre MSW e API real ao testar
 *   integração com backend.
 * - PROD: "modo demo" — apresentar a app sem backend (mocks) ou apontar
 *   para uma API alternativa (ex.: tenant específico) sem novo deploy.
 *
 * Como funciona a troca:
 * 1. Usuário escolhe um modo (e opcionalmente uma URL para 'api').
 * 2. Salva a preferência em `localStorage`.
 * 3. `location.reload()` — necessário porque o Service Worker do MSW
 *    precisa ser registrado/desregistrado, e o `apiClient` lê a baseUrl
 *    no boot. Reload é mais seguro do que tentar parar/iniciar o worker
 *    em runtime e arriscar requests intermediários sem handler.
 */
export function ModeSwitcherPopover() {
  const env = readAppEnvFresh()
  const [open, setOpen] = useState(false)
  const [draftMode, setDraftMode] = useState<AppMode>(env.mode)
  const [draftUrl, setDraftUrl] = useState(env.apiBaseUrl)

  function handleApply() {
    writeStoredPreference({
      mode: draftMode,
      apiBaseUrl: draftMode === 'api' ? draftUrl.trim() : undefined,
    })
    invalidateAppEnvCache()
    window.location.reload()
  }

  function handleReset() {
    clearStoredPreference()
    invalidateAppEnvCache()
    window.location.reload()
  }

  const dirty = draftMode !== env.mode || draftUrl.trim() !== env.apiBaseUrl
  const apiUrlValid = draftMode === 'mocks' || draftUrl.trim().length > 0

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (next) {
          invalidateAppEnvCache()
          const fresh = readAppEnvFresh()
          const pref = readStoredPreference()
          setDraftMode(fresh.mode)
          setDraftUrl(
            pref?.apiBaseUrl?.trim() || fresh.apiBaseUrl || 'http://localhost:3100/api',
          )
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Modo atual: ${env.mode}. Clique para trocar.`}
          className="pointer-events-auto fixed bottom-3 right-3 z-40 flex items-center gap-2 rounded-md border border-border bg-popover/90 px-2.5 py-1.5 font-mono text-[11px] text-popover-foreground opacity-60 shadow-elevated backdrop-blur transition-[opacity,border-color] hover:border-border-strong hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
        >
          <ModeIcon mode={env.mode} />
          <span className="font-semibold uppercase tracking-wider">
            {env.mode}
          </span>
          {env.mode === 'api' && env.apiBaseUrl ? (
            <span className="text-muted-foreground">{shortHost(env.apiBaseUrl)}</span>
          ) : null}
          {env.hasOverride ? (
            <span
              aria-label="Override ativo"
              title="Override do usuário ativo (preferência salva)"
              className="h-1.5 w-1.5 rounded-full bg-primary"
            />
          ) : null}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        side="top"
        sideOffset={8}
        className="w-80"
      >
        <div className="space-y-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Modo de execução
            </div>
            <h3 className="mt-1 text-sm font-semibold">
              Alternar fonte de dados
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              {env.hasOverride
                ? 'Preferência salva está ativa. O .env do build foi sobrescrito.'
                : 'Usando o default do .env. Salve uma preferência para sobrescrever.'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <ModeButton
              mode="mocks"
              chosen={draftMode}
              icon={Database}
              label="Mocks"
              onChoose={() => setDraftMode('mocks')}
            />
            <ModeButton
              mode="api"
              chosen={draftMode}
              icon={Wifi}
              label="API real"
              onChoose={() => setDraftMode('api')}
            />
          </div>

          {draftMode === 'api' ? (
            <div className="space-y-1.5">
              <Label
                htmlFor="switcher-api-url"
                className="text-[11px] uppercase tracking-wider text-muted-foreground"
              >
                URL base
              </Label>
              <Input
                id="switcher-api-url"
                type="url"
                placeholder="http://localhost:3100/api"
                value={draftUrl}
                onChange={(e) => setDraftUrl(e.target.value)}
                className="h-9 text-xs"
              />
            </div>
          ) : null}

          <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReset}
              disabled={!env.hasOverride}
              className="text-xs"
            >
              <RotateCcw className="mr-1.5 h-3 w-3" />
              Limpar override
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleApply}
              disabled={!dirty || !apiUrlValid}
            >
              Aplicar e recarregar
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

function ModeIcon({ mode }: { mode: AppMode }) {
  const cls =
    mode === 'mocks'
      ? 'flex h-4 w-4 items-center justify-center rounded-sm bg-warning/20 text-warning'
      : 'flex h-4 w-4 items-center justify-center rounded-sm bg-success/20 text-success'
  return (
    <span aria-hidden className={cls}>
      {mode === 'mocks' ? (
        <Database className="h-3 w-3" />
      ) : (
        <Wifi className="h-3 w-3" />
      )}
    </span>
  )
}

interface ModeButtonProps {
  mode: AppMode
  chosen: AppMode
  icon: typeof Database
  label: string
  onChoose: () => void
}

function ModeButton({
  mode,
  chosen,
  icon: Icon,
  label,
  onChoose,
}: ModeButtonProps) {
  const selected = mode === chosen
  return (
    <button
      type="button"
      onClick={onChoose}
      aria-pressed={selected}
      className={cn(
        'flex flex-col items-center gap-1.5 rounded-md border p-3 text-xs transition-colors',
        selected
          ? 'border-primary bg-primary/5 text-foreground'
          : 'border-border bg-surface/40 text-muted-foreground hover:border-border-strong hover:text-foreground',
      )}
    >
      <Icon className={cn('h-4 w-4', selected ? 'text-primary' : '')} />
      <span className="font-medium">{label}</span>
    </button>
  )
}

function shortHost(url: string): string {
  try {
    const u = new URL(url)
    return u.host
  } catch {
    return url
  }
}
